import { useToast } from "@dash/toast";
import { useContext, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";
import { Serializers, type Email } from "@megaron/serializers";
import { Uuid } from "@megaron/uuid";
import type { TicketCategory } from "@megaron/support-contracts";

import { AttachmentEntry } from "@megarax/crm-customers";

import type { TicketProductDraft } from "../products/SupportTicketProductsField";
import {
  buildSupportCreateTicketContactOptions,
  buildTagsFromSelectedReasons,
} from "./supportTicketReasonCategories";
import { joinFullName } from "./supportReporterName";
import { useSupportCreateTicketReporterPrefill } from "./useSupportCreateTicketReporterPrefill";

export type SupportCreateTicketFormParams = {
  listPath: string;
  customerUuid?: Uuid;
  defaultReporterEmail?: string;
  defaultReporterFirstName?: string;
  defaultReporterLastName?: string;
  defaultReporterName?: string;
};

function appendUniqueCustomerUuid(current: Uuid[], customerUuid: Uuid): Uuid[] {
  if (current.some((id) => id.toString() === customerUuid.toString())) return current;
  return [...current, customerUuid];
}

export function useSupportCreateTicketForm(params: SupportCreateTicketFormParams) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { iamUser } = useContext(IamAuthContext);
  const support = useClientManager("support");
  const crm = useClientManager("crm");

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [reporterFirstName, setReporterFirstName] = useState(params.defaultReporterFirstName ?? "");
  const [reporterLastName, setReporterLastName] = useState(params.defaultReporterLastName ?? "");
  const [reporterEmail, setReporterEmail] = useState(params.defaultReporterEmail ?? "");
  const [selectedCustomerUuid, setSelectedCustomerUuidState] = useState<Uuid | undefined>(params.customerUuid);
  const [selectedContact, setSelectedContact] = useState("");
  const [attachments, setAttachments] = useState<AttachmentEntry[]>([]);
  const [selectedReasonIds, setSelectedReasonIds] = useState<string[]>([]);
  const [category, setCategory] = useState<TicketCategory | null>(null);
  const [products, setProducts] = useState<TicketProductDraft[]>([]);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [assignedCustomerUuids, setAssignedCustomerUuids] = useState<Uuid[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const customerQuery = crm.getCustomer().useQuery(
    { customerId: params.customerUuid ?? Uuid("00000000-0000-0000-0000-000000000000") },
    { enabled: !!params.customerUuid },
  );
  const contactsQuery = crm.customerContactsQuery().useQuery(
    { customerId: params.customerUuid ?? Uuid("00000000-0000-0000-0000-000000000000") },
    { enabled: !!params.customerUuid },
  );

  const contactOptions = useMemo(
    () =>
      buildSupportCreateTicketContactOptions(!!params.customerUuid, customerQuery.data, contactsQuery.data ?? []),
    [contactsQuery.data, customerQuery.data, params.customerUuid],
  );

  const onAddAssignedCustomer = (customerUuid: Uuid) => {
    setAssignedCustomerUuids((current) => appendUniqueCustomerUuid(current, customerUuid));
  };

  const setSelectedCustomerUuid = (customerUuid: Uuid | undefined) => {
    setSelectedCustomerUuidState(customerUuid);
  };

  useSupportCreateTicketReporterPrefill(
    params,
    customerQuery.data,
    contactOptions,
    reporterFirstName,
    reporterLastName,
    reporterEmail,
    setReporterFirstName,
    setReporterLastName,
    setReporterEmail,
    setSelectedCustomerUuid,
    setSelectedContact,
  );

  const createTicketMutation = support.createTicket().useMutation();
  const addTicketCustomerMutation = support.addTicketCustomer().useMutation();

  const isBusy = isSubmitting;
  const hasCustomerContext = !!params.customerUuid;

  const onContactChange = (value: string) => {
    const selected = contactOptions.find((option) => option.value === value);
    if (!selected) return;
    setSelectedContact(selected.value);
  };

  const onRemoveAssignedCustomer = (customerUuid: Uuid) => {
    setAssignedCustomerUuids((current) =>
      current.filter((id) => id.toString() !== customerUuid.toString()),
    );
  };

  const onCreate = async () => {
    const trimmedSubject = subject.trim();
    const trimmedReporterEmail = reporterEmail.trim();
    const trimmedReporterName = joinFullName(reporterFirstName, reporterLastName);
    const trimmedMessage = message.trim();
    if (!trimmedSubject || !trimmedMessage) {
      toast.error("Uzupełnij tytuł i wiadomość");
      return;
    }
    let resolvedReporterEmail: Email | null = null;
    if (trimmedReporterEmail) {
      const reporterEmailResult = Serializers.email.deserialize(trimmedReporterEmail);
      if (reporterEmailResult.isFailure) {
        toast.error("Podaj poprawny email zgłaszającego");
        return;
      }
      resolvedReporterEmail = reporterEmailResult.value;
    }
    if (hasCustomerContext && !resolvedReporterEmail) {
      toast.error("Uzupełnij email zgłaszającego");
      return;
    }
    const tags = buildTagsFromSelectedReasons(selectedReasonIds);
    const assignedWithoutReporter = selectedCustomerUuid
      ? assignedCustomerUuids.filter((id) => id.toString() !== selectedCustomerUuid.toString())
      : assignedCustomerUuids;
    setIsSubmitting(true);
    try {
      const ticket = await createTicketMutation.mutateAsync({
        subject: trimmedSubject,
        message: trimmedMessage,
        reporterEmail: resolvedReporterEmail,
        reporterName: trimmedReporterName || (trimmedReporterEmail ? trimmedReporterEmail.split("@")[0] : ""),
        sourceMailbox: null,
        status: "new",
        priority: null,
        category,
        tags: tags.length > 0 ? tags : null,
        assigneeEmails: iamUser?.email ? [iamUser.email] : null,
        attachments: attachments.length > 0 ? attachments : null,
        products: products.length > 0 ? products : null,
        invoiceNumber: invoiceNumber.trim() || null,
        orderNumber: orderNumber.trim() || null,
        reporterCustomerUuid: selectedCustomerUuid ?? null,
      });
      if (assignedWithoutReporter.length > 0) {
        await Promise.all(
          assignedWithoutReporter.map((customerUuid) =>
            addTicketCustomerMutation.mutateAsync({ id: ticket.id, customerUuid, isReporter: false }),
          ),
        );
      }
      queryClient.invalidateQueries({ queryKey: ["support", "ticketsList"] });
      toast.info("Utworzono zgłoszenie");
      navigate(`${params.listPath}/${ticket.id}`);
    } catch {
      toast.error("Nie udało się utworzyć zgłoszenia");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    subject,
    setSubject,
    message,
    setMessage,
    reporterFirstName,
    setReporterFirstName,
    reporterLastName,
    setReporterLastName,
    reporterEmail,
    setReporterEmail,
    selectedCustomerUuid,
    setSelectedCustomerUuid,
    selectedContact,
    onContactChange,
    contactOptions,
    hasCustomerContext,
    assignedCustomerUuids,
    onAddAssignedCustomer,
    onRemoveAssignedCustomer,
    attachments,
    setAttachments,
    selectedReasonIds,
    setSelectedReasonIds,
    category,
    setCategory,
    products,
    setProducts,
    invoiceNumber,
    setInvoiceNumber,
    orderNumber,
    setOrderNumber,
    onCreate,
    isBusy,
  };
}
